// Pixar Talking Ad's form (live's `pixar-talking-ad-form/library-view`, spec G14-pixar-talking-ad,
// captures 147 to 157 and 209 to 224): live's staged form in two approvals. The script stage picks
// the product (one of the brand's, or an uploaded packshot) and the character (an AI Actor or a
// photo, or none), writes the script (the server's pack reader and director, or the member) and
// approves it; Cast & set shows the five images (`PixarCast.tsx`). The draft is the server's
// (`pixar-draft.ts`). "Generate final video" starts the run (G14 PR 18c, `pixar_talking_ad.py`).
import { Group, Loader, Stack, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { Monitor, PanelBottom, Smartphone } from "lucide-react";
import { useState, type ReactNode } from "react";

import { useGetCatalog } from "../../../api/generated/endpoints/generation/generation";
import type { Job, PixarScript, PixarTalkingAdInputsResolution } from "../../../api/generated/model";
import {
  VideoTemplateQuote,
  VideoTemplateRequest,
  pixarTalkingAdInputsCtaMax,
  pixarTalkingAdInputsProblemMax,
  pixarTalkingAdInputsPronunciationMax,
  type PixarTalkingAdInputsOutput,
} from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { ActorsComposer } from "../../../platform/actors/composer/ActorsComposer";
import { actorModels } from "../../../platform/actors/composer/GenerateWithText";
import { pickedOfActor, type PickedActor } from "../../../platform/actors/picked";
import { useMakings } from "../../../platform/actors/use-makings";
import { Choice } from "../../../ui/Choice";
import { GlassPanel } from "../../../ui/GlassPanel";
import { ReuseSwitch, type TemplateFormProps } from "./kit";
import { CastTile, sourceKey, type PickedPictures } from "./PodcastTiles";
import { InputChip, ScriptSection } from "./PodcastScript";
import { PixarCastStage } from "./PixarCast";
import { Submit, Subtitles } from "./staged";
import { StreetProductTile, type ProductWords } from "./StreetTiles";
import { drawnStills, refusedFrames, usePixarDraft, videoBody, type DraftWork, type VideoSettings } from "./pixar-draft";
import {
  ACTOR_PENDING,
  GENERATE_SCRIPT,
  PICK_PRODUCT,
  STILLS_PENDING,
  UNREADABLE_PACK,
  blankScript,
  maxWordsAt,
  scriptIssue,
  scriptMeta,
  scriptProblems,
} from "./pixar-script";
import { sameLineTexts } from "./street-script";
import classes from "./podcast.module.css";

/** Live's words (`library-view/constants.ts`). */
const PRODUCT_WORDS: ProductWords = {
  tile:
    "The product and the exact photo of it the ad is built on. Pick one of your products and its " +
    "packshot, or upload a packshot — the pack the character picks up and holds.",
  name:
    "The product name the character says and the end card shows. Leave empty and it is read off " +
    "the packshot.",
  text:
    "A description or the product page. Every claim in the script is quoted from it — leave empty " +
    "and the words printed on the pack are used.",
};
const CHARACTER_TOOLTIP =
  "The one character who talks in the ad. Pick an AI Actor or a photo and it becomes an animated " +
  "character with the same face, hair and clothes — leave empty and the director designs one for " +
  "your product's buyer.";
const SCRIPT_TOOLTIP =
  "8 fixed lines spoken by the character: lines 1–4 end as they pick up the product, lines 5–8 " +
  "give the proof and the call to action. Edit any line before approving — every product fact is " +
  "quoted from the product text.";
const SUBTITLES_TOOLTIP =
  "Burned-in captions over the talk in short lines: turn them on or off, pick where they sit and " +
  "how they look.";
const END_CARD_TOOLTIP = "A 4-second product card at the end while the character's voice reads its headline.";
const RESOLUTION_TOOLTIP = "Resolution of every clip. 720p costs about 40% of 1080p.";
const FORMAT_TOOLTIP = "Every frame and clip is vertical 9:16.";
const WRITE_SCRIPT = "Write the 8 lines yourself";
const SCRIPT_HINT = { wand: "to write the 8 lines from the product, or", pencil: "to write them yourself." };
const APPROVE_SCRIPT = "Approve script";
const GENERATE_VIDEO = "Generate final video";
const HANDOFF_NOTICE = "Your Pixar talking ad is finishing in My Generations — you can close this panel now.";
const RESOLUTIONS: readonly PixarTalkingAdInputsResolution[] = ["1080p", "720p"];
const CHIPS = [
  {
    field: "problem",
    label: "Problem",
    placeholder: "The afternoon sugar crash (optional)",
    tooltip:
      'The everyday problem the character talks about, e.g. "the afternoon sugar crash". Leave ' +
      "empty and the director takes it from the product text.",
    maxLength: pixarTalkingAdInputsProblemMax,
  },
  {
    field: "cta",
    label: "CTA",
    placeholder: "Shop now (optional)",
    tooltip: "The button label on the end card. Leave empty and the director writes a short one.",
    maxLength: pixarTalkingAdInputsCtaMax,
  },
  {
    field: "pronunciation",
    label: "Pronunciation",
    placeholder: "GO-lee (optional)",
    tooltip:
      "How your brand name is said, e.g. GO-lee. Leave empty and the director decides what needs a " +
      "respelling.",
    maxLength: pixarTalkingAdInputsPronunciationMax,
  },
] as const;

/** Live's progress line under the form (`run-progress.ts`). */
function progressOf(work: DraftWork, drawn: number, total: number): string | null {
  if (work.reading) return "Reading the pack";
  if (work.writing) return "Writing the script";
  if (work.planning) return "Planning the shots";
  return work.drawing ? `Generating images · ${drawn}/${total}` : null;
}

/**
 * Live's form: "Reuse example" (off and disabled: the card has no example, Pixar Talking Ad-6),
 * the stage, and in the footer the subtitles, the end card, the resolution, the 9:16 format and
 * the round start, "Approve script" then "Generate final video", whose tooltip says what it waits
 * for. The subtitles and the resolution stay editable after Approve script, as live's; the end
 * card does not.
 */
export function PixarTalkingAdForm(props: TemplateFormProps) {
  const { teamId, brandId, above, onStarted, example } = props;
  const [collapsed, setCollapsed] = useState(false);
  const flow = usePixarDraft(teamId, brandId);
  const catalog = useGetCatalog().data;
  const [composing, setComposing] = useState(false);
  const [making, setMaking] = useState<{ jobId: string; failed: boolean } | null>(null);
  const [pictures, setPictures] = useState<PickedPictures>({});
  const [baseline, setBaseline] = useState<PixarScript | null | undefined>(undefined);
  const [submitted, setSubmitted] = useState(false);
  const [wasWriting, setWasWriting] = useState(false);
  const [productAsked, setProductAsked] = useState(false);
  const [settings, setSettings] = useState<VideoSettings | null>(null);
  // The run started here, until the draft reads it back ended (the hand-off shows meanwhile).
  const [started, setStarted] = useState<string | null>(null);
  const { form, draft } = flow;
  const writing = flow.work.reading || flow.work.writing;
  const approved = draft !== undefined && draft.stage !== "script";
  const shownSettings: VideoSettings | null =
    form === null
      ? null
      : ((approved ? settings : null) ?? {
          subtitles: form.inputs.subtitles,
          caption_position: form.inputs.caption_position,
          caption_style_id: form.inputs.caption_style_id,
          resolution: form.inputs.resolution,
        });
  const video = draft === undefined || shownSettings === null ? null : videoBody(draft, shownSettings);
  const videoRequest = VideoTemplateRequest.safeParse(video);
  const videoQuote = VideoTemplateQuote.safeParse(video);
  const began = (job: Job) => {
    setStarted(job.id);
    flow.refresh();
    onStarted(job);
  };
  const videoStart = usePricedStart({
    teamId,
    brandId,
    quote: videoQuote.success ? videoQuote.data : undefined,
    request: videoRequest.success ? videoRequest.data : undefined,
    onStarted: began,
  });
  // Live's refused-take notices on the frames of a clip the last run's video model refused, and
  // on a refused end card "Build without end card": the run without it, quoted only then
  // (Pixar Talking Ad-2; the takes the next run keeps are `videoBody`'s).
  const frames = draft === undefined ? null : drawnStills(draft);
  const flags = draft === undefined || frames === null ? {} : refusedFrames(draft.run, frames);
  const withoutCard =
    draft === undefined || shownSettings === null || flags.end_card === undefined
      ? null
      : videoBody(draft, shownSettings, true);
  const withoutCardRequest = VideoTemplateRequest.safeParse(withoutCard);
  const withoutCardQuote = VideoTemplateQuote.safeParse(withoutCard);
  const withoutCardStart = usePricedStart({
    teamId,
    brandId,
    quote: withoutCard !== null && withoutCardQuote.success ? withoutCardQuote.data : undefined,
    request: withoutCard !== null && withoutCardRequest.success ? withoutCardRequest.data : undefined,
    onStarted: began,
  });
  // A run of the draft's under way: the one it reads back open, or one started here it has not
  // read back yet (`started` goes once it does, or with Start over, so a run that ended holds
  // nothing up). The footer locks meanwhile, as live's does while something runs.
  if (started !== null && draft?.run?.job_id === started) setStarted(null);
  const openRun = draft?.run !== undefined && draft.run !== null && !draft.run.ended;
  const running = openRun || (started !== null && draft?.run?.job_id !== started);
  const starting = videoStart.start.isPending || withoutCardStart.start.isPending;
  if (!approved && settings !== null) setSettings(null);
  if (draft !== undefined && baseline === undefined) setBaseline(draft.script);
  if (writing !== wasWriting) {
    setWasWriting(writing);
    if (!writing && draft !== undefined && draft.script_director !== "failed") setBaseline(draft.script);
  }

  const setInputs = (change: Partial<PixarTalkingAdInputsOutput>) => flow.editInputs(change);
  const pick = (actor: PickedActor) => {
    setPictures((current) => ({ ...current, [sourceKey(actor.source)]: { url: actor.imageUrl, name: actor.name } }));
    setInputs({ character: actor.source });
  };
  useMakings({
    teamId,
    brandId,
    makings: making === null ? [] : [making],
    onMade: (_jobId, made) => {
      setMaking(null);
      pick(pickedOfActor(made));
    },
    onFailed: () => setMaking((current) => (current === null ? null : { ...current, failed: true })),
  });

  const panel = (body: ReactNode, footer?: ReactNode) => (
    <GlassPanel
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      above={above}
      header={<ReuseSwitch example={null} checked={false} onChange={() => undefined} />}
      summary={<Text size="sm">Pixar Talking Ad</Text>}
      footer={footer}
    >
      {body}
    </GlassPanel>
  );
  if (flow.read.isError) return panel(<ErrorAlert error={flow.read.error} />);
  if (draft === undefined || form === null || shownSettings === null) {
    return panel(<Loader size="sm" aria-label="Loading the draft" />);
  }

  if (composing && catalog !== undefined) {
    const close = () => setComposing(false);
    return (
      <ActorsComposer
        teamId={teamId}
        brandId={brandId}
        catalog={catalog}
        onStarted={(job) => {
          setMaking({ jobId: job.id, failed: false });
          close();
        }}
        place={{
          outputs: 1,
          uploadMost: 1,
          onCancel: close,
          onCreated: (made) => {
            const actor = made[0];
            if (actor !== undefined) pick(pickedOfActor(actor));
            close();
          },
        }}
      />
    );
  }

  const { inputs, script } = form;
  const changeSettings = (change: Partial<VideoSettings>) => {
    if (approved) setSettings({ ...shownSettings, ...change });
    else setInputs(change);
  };
  const locked = !flow.editable;
  const work = flow.work;
  const drawn = draft.stills.filter((each) => each.status === "done").length;
  const progress = progressOf(work, drawn, draft.stills.length);
  const issue = scriptIssue(script);
  const edited = script !== null && !sameLineTexts(script, baseline ?? null);
  const shownIssue = script !== null && !writing && (edited || submitted) ? issue : null;
  const makers = catalog === undefined ? [] : actorModels(catalog.image_models);

  let submit;
  if (approved) {
    const ready = draft.stills.length > 0 && drawn === draft.stills.length;
    submit = (
      <Submit
        label={GENERATE_VIDEO}
        reasons={ready ? [] : [STILLS_PENDING]}
        disabled={!ready || flow.busy || !videoStart.canStart || starting || running}
        loading={videoStart.start.isPending}
        onClick={videoStart.startJob}
      />
    );
  } else {
    const reasons = [
      ...(inputs.product ? [] : [PICK_PRODUCT]),
      ...(script === null ? [GENERATE_SCRIPT] : []),
      // A failed AI Actor waits in its tile, shown failed, for its remove: it holds nothing up.
      ...(making !== null && !making.failed ? [ACTOR_PENDING] : []),
    ];
    submit = (
      <Submit
        label={APPROVE_SCRIPT}
        reasons={reasons}
        disabled={reasons.length > 0 || issue !== null || flow.busy}
        loading={flow.approving}
        onClick={() => {
          setSubmitted(true);
          flow.approve();
        }}
      />
    );
  }

  const footer = (
    <Group justify="space-between" wrap="nowrap" gap={12}>
      <div className={classes.settings}>
        <Subtitles
          on={shownSettings.subtitles}
          position={shownSettings.caption_position}
          disabled={flow.busy || running}
          tooltip={SUBTITLES_TOOLTIP}
          onChange={(change) => changeSettings(change)}
        />
        <Tooltip label={END_CARD_TOOLTIP} multiline maw={280} withArrow>
          <UnstyledButton
            className={classes.setting}
            data-on={inputs.end_card || undefined}
            aria-pressed={inputs.end_card}
            disabled={locked}
            onClick={() => setInputs({ end_card: !inputs.end_card })}
          >
            <PanelBottom size={16} strokeWidth={1.5} aria-hidden />
            {inputs.end_card ? "End card on" : "End card off"}
          </UnstyledButton>
        </Tooltip>
        <Choice
          variant="footer"
          setting="Resolution"
          icon={<Monitor size={16} strokeWidth={1.5} />}
          tip={RESOLUTION_TOOLTIP}
          width={88}
          value={shownSettings.resolution}
          options={RESOLUTIONS.map((value) => ({ value, label: value }))}
          disabled={flow.busy || running}
          onChange={(next) => {
            const resolution = RESOLUTIONS.find((each) => each === next);
            if (resolution !== undefined) changeSettings({ resolution });
          }}
        />
        <Tooltip label={FORMAT_TOOLTIP} multiline maw={280} withArrow>
          <span className={classes.setting}>
            <Smartphone size={16} strokeWidth={1.5} aria-hidden />
            9:16
          </span>
        </Tooltip>
      </div>
      {submit}
    </Group>
  );

  const refused =
    videoStart.start.error ??
    videoStart.quoteError ??
    withoutCardStart.start.error ??
    withoutCardStart.quoteError ??
    flow.failure;
  const status = (
    <>
      {draft.problem === null ? null : (
        <Text className={classes.problem} role="alert">
          {draft.problem}
        </Text>
      )}
      {refused === null ? null : <ErrorAlert error={refused} />}
      {progress === null ? null : (
        <output className={classes.progress}>
          <Loader size={14} aria-hidden />
          {progress}
        </output>
      )}
      {running && approved ? (
        <Text component="output" size="sm">
          Generation started. {HANDOFF_NOTICE}
        </Text>
      ) : null}
    </>
  );
  if (approved) {
    return panel(
      <div className={classes.content}>
        <PixarCastStage
          stills={draft.stills}
          endCard={inputs.end_card}
          planning={work.planning}
          busy={flow.busy || running}
          resetting={flow.resetting}
          flags={flags}
          onBuildWithoutEndCard={
            withoutCardStart.canStart && !starting && !running ? withoutCardStart.startJob : null
          }
          onRegenerate={flow.regenerate}
          onStartOver={() => {
            setStarted(null);
            flow.startOver();
          }}
        />
        {status}
      </div>,
      footer,
    );
  }

  const warnings = [
    ...(draft.pack_warning ? [UNREADABLE_PACK] : []),
    ...(draft.script_problems.length > 0 && script !== null ? [scriptProblems(draft.script_problems)] : []),
  ];
  const body = (
    <div className={classes.content}>
      <Stack gap={12}>
        <div className={classes.tiles}>
          <StreetProductTile
            teamId={teamId}
            brandId={brandId}
            pick={inputs.product ?? null}
            typed={{ product_name: inputs.product_name, product_text: inputs.product_text }}
            disabled={locked}
            missing={productAsked}
            exampleUrls={example?.urls ?? {}}
            words={PRODUCT_WORDS}
            onChange={(product, typed) => setInputs({ product, ...typed, product_category: "" })}
          />
          <CastTile
            teamId={teamId}
            brandId={brandId}
            label="Character"
            tooltip={CHARACTER_TOOLTIP}
            source={inputs.character ?? null}
            making={making}
            pictures={pictures}
            disabled={locked}
            onMakeActor={makers.length === 0 ? null : () => setComposing(true)}
            onPick={pick}
            onRemove={() => {
              setMaking(null);
              setInputs({ character: null });
            }}
          />
        </div>
        <div className={classes.chips}>
          {CHIPS.map((chip) => (
            <InputChip
              key={chip.field}
              label={chip.label}
              value={inputs[chip.field]}
              placeholder={chip.placeholder}
              tooltip={chip.tooltip}
              maxLength={chip.maxLength}
              disabled={locked}
              onChange={(value) => {
                const change: Partial<PixarTalkingAdInputsOutput> = {};
                change[chip.field] = value;
                setInputs(change);
              }}
            />
          ))}
        </div>
        <ScriptSection
          lines={script === null ? null : script.lines.map((line) => ({ ...line, speaker: null }))}
          meta={script === null ? null : scriptMeta(script.lines)}
          tooltip={SCRIPT_TOOLTIP}
          primary={null}
          maxWords={maxWordsAt}
          hint={SCRIPT_HINT}
          writeLabel={WRITE_SCRIPT}
          writing={writing}
          locked={locked}
          issue={shownIssue}
          warning={warnings.length === 0 ? null : warnings.join(" ")}
          onGenerate={() => {
            if (!inputs.product) {
              setProductAsked(true);
              return;
            }
            flow.generateScript();
          }}
          onWrite={() => {
            const blank = blankScript();
            setBaseline(blank);
            flow.edit({ script: blank });
          }}
          onLine={(index, text) => {
            if (script === null) return;
            flow.edit({
              script: { ...script, lines: script.lines.map((line, at) => (at === index ? { ...line, text } : line)) },
            });
          }}
        />
      </Stack>
      {status}
    </div>
  );
  return panel(body, footer);
}
